import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { deleteUserData, upsertUserData, logout } from "../redux/authSlice";
import logo from "../assets/app/logo.png";

import {
  Lock,
  Shield,
  ShieldCheck,
  LogOut,
  Trash2,
  Power,
  AlertTriangle,
  Mail,
  ChevronRight,
  ArrowLeft,
  X,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════════════
   THEME: classy black & red, same tokens as Shop / Profile / EditProfile
   ═══════════════════════════════════════════════════════════════════ */
const A = {
  ink: "#000", red: "#E10000", redDeep: "#B30000",
  text: "#000", textSoft: "#5C5C5C", muted: "#8A8A8A",
  sand: "#F4F4F4", line: "#E5E5E5", edge: "#D4D4D4",
};

// Deployed endpoint: generates/sends a code and confirms it, distinguished
// by the `action` field ("send" | "confirm").
const VERIFY_EMAIL_PHONE_API = "https://europe-west3-alreluxe.cloudfunctions.net/verifyEmailPhone";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Jost:wght@300;400;500;600&display=swap');

.as, .as *, .as *::before, .as *::after { box-sizing:border-box; font-family:'Jost', system-ui, sans-serif; }
.as { color:${A.text}; background:#fff; min-height:100vh; -webkit-font-smoothing:antialiased; overflow-x:hidden; }
/* :where keeps this reset at zero specificity so every button class below wins */
:where(.as) button { cursor:pointer; border:none; background:none; font-family:inherit; color:inherit; }
.as button:focus-visible, .as input:focus-visible { outline:2px solid ${A.red}; outline-offset:3px; }

/* ═══ HEADER (glass) ═══ */
.as-header {
  position:sticky; top:0; z-index:100; height:76px; padding:0 20px; padding-top:env(safe-area-inset-top);
  display:flex; align-items:center; justify-content:space-between; gap:12px;
  background:rgba(255,255,255,.62); -webkit-backdrop-filter:blur(22px) saturate(180%); backdrop-filter:blur(22px) saturate(180%);
  border-bottom:1px solid rgba(0,0,0,.07); box-shadow:0 10px 30px -20px rgba(0,0,0,.35);
}
.as-round {
  position:relative; width:46px; height:46px; border-radius:50%; flex-shrink:0;
  border:1px solid ${A.ink}; background:#fff; display:flex; align-items:center; justify-content:center;
  box-shadow:0 0 0 3px #fff, 0 0 0 4px ${A.ink}; transition:all .25s;
}
.as-round:hover { border-color:${A.red}; color:${A.red}; box-shadow:0 0 0 3px #fff, 0 0 0 4px ${A.red}; }
.as-header-title { font-family:'Cormorant Garamond',serif; font-weight:500; font-size:26px; letter-spacing:.01em; white-space:nowrap; }
.as-header-title i { font-style:normal; color:${A.red}; font-size:.45em; vertical-align:super; margin-left:5px; }
.as-logo { height:30px; width:46px; object-fit:contain; display:block; flex-shrink:0; }

.as-profile-header { text-align:center; padding:34px 20px 6px; }
.as-user-name { font-family:'Cormorant Garamond',serif; font-weight:500; font-size:clamp(34px,8vw,46px); line-height:1.05; margin:0 0 6px; }
.as-user-email { font-size:13px; color:${A.textSoft}; margin:0; letter-spacing:.03em; }

.as-body { width:100%; max-width:520px; margin:0 auto; padding:26px 20px calc(80px + env(safe-area-inset-bottom)); display:flex; flex-direction:column; gap:14px; }

/* ═══ CARD: double border ═══ */
.as-card { position:relative; background:#fff; border:1px solid ${A.ink}; border-radius:24px; padding:22px 24px; transition:transform .3s, border-color .25s; }
.as-card::after { content:""; position:absolute; inset:4px; border:1px solid ${A.ink}; border-radius:20px; pointer-events:none; transition:border-color .25s; }
.as-card.clickable { cursor:pointer; }
.as-card.clickable:hover { transform:translateY(-3px); border-color:${A.red}; }
.as-card.clickable:hover::after { border-color:${A.red}; }
.as-card.clickable:hover .as-icon-bead { border-color:${A.red}; color:${A.red}; }

.as-icon-row { display:flex; align-items:center; gap:14px; min-width:0; }
.as-icon-bead { width:42px; height:42px; border-radius:50%; border:1px solid ${A.ink}; background:#fff; color:${A.ink}; display:flex; align-items:center; justify-content:center; flex-shrink:0; transition:all .25s; }
.as-section-title { font-family:'Cormorant Garamond',serif; font-weight:500; font-size:22px; line-height:1.1; }
.as-section-sub { font-size:12px; color:${A.textSoft}; margin-top:3px; letter-spacing:.02em; word-break:break-all; }
.as-row-between { display:flex; align-items:center; justify-content:space-between; gap:12px; }

.as-badge { display:inline-flex; align-items:center; gap:6px; padding:6px 12px; border-radius:999px; font-size:10px; font-weight:500; letter-spacing:.16em; text-transform:uppercase; flex-shrink:0; }
.as-badge.ok      { background:${A.ink}; color:#fff; }
.as-badge.pending { border:1px solid #CFCFCF; color:${A.textSoft}; }

.as-ghost-btn {
  display:inline-flex; align-items:center; justify-content:center; gap:7px; height:42px; padding:0 20px; border-radius:999px;
  border:1px solid ${A.ink}; background:#fff; color:${A.ink};
  font-size:11px; font-weight:500; letter-spacing:.2em; text-transform:uppercase; transition:all .25s;
}
.as-ghost-btn:hover:not(:disabled) { border-color:${A.red}; color:${A.red}; transform:translateY(-1px); }
.as-ghost-btn:disabled { opacity:.5; cursor:not-allowed; }

.as-input {
  width:100%; height:48px; border-radius:14px; border:none; padding:0 16px; font-size:15px; color:${A.text}; background:#fff;
  box-shadow:inset 0 0 0 1px ${A.edge}; outline:none; letter-spacing:.04em; transition:box-shadow .2s;
}
.as-input:hover { box-shadow:inset 0 0 0 1px ${A.ink}; }
.as-input:focus { box-shadow:inset 0 0 0 1.5px ${A.ink}, 0 0 0 3px rgba(0,0,0,.08); }
.as-input::placeholder { color:#A3A3A3; }

.as-btn {
  display:flex; align-items:center; justify-content:center; gap:9px; height:52px; padding:0 24px; border-radius:999px; border:1px solid ${A.ink};
  font-size:11.5px; font-weight:500; letter-spacing:.22em; text-transform:uppercase;
  transition:background .25s, border-color .25s, color .25s, transform .25s;
}
.as-btn.primary { background:${A.ink}; color:#fff; }
.as-btn.primary:hover:not(:disabled) { background:${A.red}; border-color:${A.red}; transform:translateY(-1px); }
.as-btn.primary:active:not(:disabled) { background:${A.redDeep}; border-color:${A.redDeep}; }
.as-btn.cancel  { background:#fff; color:${A.ink}; }
.as-btn.cancel:hover:not(:disabled) { border-color:${A.red}; color:${A.red}; }
.as-btn.danger  { background:${A.red}; border-color:${A.red}; color:#fff; }
.as-btn.danger:hover:not(:disabled) { background:${A.redDeep}; border-color:${A.redDeep}; transform:translateY(-1px); }
.as-btn.success { background:#fff; color:${A.ink}; }
.as-btn.success:hover:not(:disabled) { border-color:${A.red}; color:${A.red}; transform:translateY(-1px); }
.as-btn:disabled { opacity:.5; cursor:not-allowed; }

/* ═══ MODALS ═══ */
.as-overlay { position:fixed; inset:0; background:rgba(0,0,0,.5); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
.as-alert-card { position:relative; background:#fff; border:1px solid ${A.ink}; border-radius:30px; padding:34px 28px 28px; text-align:center; width:100%; max-width:360px; box-shadow:0 30px 80px -20px rgba(0,0,0,.5); }
.as-alert-card::after { content:""; position:absolute; inset:6px; border:1px solid ${A.ink}; border-radius:24px; pointer-events:none; }
.as-alert-title { font-family:'Cormorant Garamond',serif; font-weight:500; font-size:30px; line-height:1.05; margin:16px 0 8px; }
.as-alert-text  { font-size:14px; color:${A.textSoft}; margin:0 0 22px; line-height:1.55; }
.as-alert-actions { display:flex; gap:10px; }
.as-alert-actions .as-btn { flex:1; padding:0 10px; letter-spacing:.16em; }

.as-icon-wrap { width:60px; height:60px; border-radius:50%; display:flex; align-items:center; justify-content:center; margin:0 auto; border:1px solid ${A.ink}; }
.as-icon-wrap.danger { border-color:${A.red}; background:#FFF2F2; }

.as-session-row { display:flex; align-items:center; gap:14px; padding:14px 16px; border-radius:16px; border:1px solid ${A.line}; margin-bottom:12px; }
.as-session-dot  { width:9px; height:9px; border-radius:50%; background:${A.ink}; flex-shrink:0; box-shadow:0 0 0 3px rgba(0,0,0,.12); }
.as-session-name { font-family:'Cormorant Garamond',serif; font-weight:500; font-size:20px; line-height:1.1; }
.as-session-meta { font-size:12px; color:${A.textSoft}; margin-top:2px; }

.as-modal-close { width:36px; height:36px; border-radius:50%; border:1px solid ${A.ink}; background:#fff; display:flex; align-items:center; justify-content:center; flex-shrink:0; transition:all .2s; }
.as-modal-close:hover { border-color:${A.red}; color:${A.red}; }

@media (prefers-reduced-motion: reduce) {
  .as *, .as *::before, .as *::after { transition:none !important; animation:none !important; }
}
`;

const maskEmail = (email: string) => {
  const [user, domain] = email.split("@");
  if (!domain) return email;
  const maskedUser = user.length <= 2 ? user + "***" : user.slice(0, 2) + "***";
  return `${maskedUser}@${domain}`;
};

// Best-effort "which browser/OS is this" label for the current session.
// There's no multi-device session store yet, so this only ever describes
// the device the person is looking at right now.
const getDeviceLabel = () => {
  const ua = navigator.userAgent;
  const browser =
    /Edg\//.test(ua) ? "Edge" :
    /Chrome\//.test(ua) ? "Chrome" :
    /Firefox\//.test(ua) ? "Firefox" :
    /Safari\//.test(ua) ? "Safari" : "Browser";
  const os =
    /Windows/.test(ua) ? "Windows" :
    /Mac OS/.test(ua) ? "macOS" :
    /Android/.test(ua) ? "Android" :
    /iPhone|iPad/.test(ua) ? "iOS" : "";
  return os ? `${browser} on ${os}` : browser;
};

const AccountSecurity: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { user } = useSelector((state: RootState) => state.auth);
  const { userId } = useParams<{ userId: string }>();

  const [editingPassword, setEditingPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // ── Email verification: send + confirm against the deployed API ──
  const [sendingCode, setSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [verifyCode, setVerifyCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const firstName = user?.firstName || "";
  const lastName  = user?.lastName  || "";
  const email     = user?.email ? maskEmail(user.email) : "hidden@mail.com";
  const isEmailVerified = user?.verifyEmail === true;
  const isDeactivated   = user?.isDisabled === true;

  /* DELETE ACCOUNT */
  const handleDelete = async () => {
    try {
      setLoading(true);
      await dispatch(deleteUserData()).unwrap();   // removes the user doc and the Firebase Auth account
      dispatch(logout());
      alert("Account deleted.");
      navigate("/AuthSwitch");
    } catch (err: any) {
      alert(err?.message || "Failed to delete account.");
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  /* DEACTIVATE ACCOUNT */
  const handleDeactivate = async () => {
    try {
      setLoading(true);
      await dispatch(upsertUserData({ isDisabled: true })).unwrap();
      alert("Your account has been deactivated.");
      setShowDeactivateConfirm(false);
    } catch (err: any) {
      alert(err?.message || "Failed to deactivate account.");
    } finally {
      setLoading(false);
    }
  };

  /* REACTIVATE ACCOUNT */
  const handleReactivate = async () => {
    try {
      setLoading(true);
      await dispatch(upsertUserData({ isDisabled: false })).unwrap();
      alert("Account reactivated successfully.");
    } catch (err: any) {
      alert(err?.message || "Failed to reactivate account.");
    } finally {
      setLoading(false);
    }
  };

  /* EMAIL VERIFICATION: send code */
  const handleSendCode = async () => {
    if (!user?.email || !userId) return;
    setSendingCode(true);
    setVerifyError(null);
    try {
      const res = await fetch(VERIFY_EMAIL_PHONE_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "send", type: "email", userId, email: user.email }),
      });
      if (!res.ok) throw new Error(await res.text());
      setCodeSent(true);
    } catch (err: any) {
      setVerifyError(err?.message || "Couldn't send the code. Try again.");
    } finally {
      setSendingCode(false);
    }
  };

  /* EMAIL VERIFICATION: confirm code */
  const handleConfirmCode = async () => {
    if (!userId || !verifyCode.trim()) return;
    setVerifying(true);
    setVerifyError(null);
    try {
      const res = await fetch(VERIFY_EMAIL_PHONE_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "confirm", type: "email", userId, code: verifyCode.trim() }),
      });
      if (!res.ok) throw new Error(await res.text());
      // The deployed API is the source of truth for whether the code was
      // right; once confirmed, upsertProfile writes verifyEmail through the
      // same canonical save path everything else in the app uses.
      await dispatch(upsertUserData({ verifyEmail: true })).unwrap();
      setCodeSent(false);
      setVerifyCode("");
    } catch (err: any) {
      setVerifyError(err?.message || "That code didn't work. Try again.");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="as">
      <style>{CSS}</style>

      <header className="as-header">
        <button type="button" className="as-round" aria-label="Back" onClick={() => navigate(-1)}>
          <ArrowLeft size={19} strokeWidth={1.8} />
        </button>
        <span className="as-header-title">Login &amp; Security<i>✦</i></span>
        <img src={logo} alt="Areluxe" className="as-logo" />
      </header>

      <div className="as-profile-header">
        <h2 className="as-user-name">{firstName} {lastName}</h2>
        <p className="as-user-email">{email}</p>
      </div>

      <div className="as-body">

        {/* PASSWORD */}
        <div className="as-card">
          <div className="as-row-between">
            <div className="as-icon-row">
              <span className="as-icon-bead"><Lock size={18} /></span>
              <span className="as-section-title">Password</span>
            </div>
            {!editingPassword && (
              <button type="button" className="as-ghost-btn" onClick={() => setEditingPassword(true)}>
                Edit
              </button>
            )}
          </div>

          <AnimatePresence>
            {editingPassword && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.25 }}
                style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 18 }}
              >
                <input type="password" placeholder="New password" className="as-input" />
                <input type="password" placeholder="Confirm password" className="as-input" />
                <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                  <button type="button" className="as-btn cancel" style={{ flex: 1, padding: "0 10px", letterSpacing: ".16em" }} onClick={() => setEditingPassword(false)}>
                    Cancel
                  </button>
                  <button type="button" className="as-btn primary" style={{ flex: 1, padding: "0 10px", letterSpacing: ".16em" }}>
                    Update
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* EMAIL VERIFICATION */}
        <div className="as-card">
          <div className="as-row-between">
            <div className="as-icon-row">
              <span className="as-icon-bead"><Mail size={18} /></span>
              <div style={{ minWidth: 0 }}>
                <div className="as-section-title">Email Verification</div>
                <div className="as-section-sub">{user?.email || "No email on file"}</div>
              </div>
            </div>
            <span className={`as-badge ${isEmailVerified ? "ok" : "pending"}`}>
              {isEmailVerified && <ShieldCheck size={12} />}
              {isEmailVerified ? "Verified" : "Unverified"}
            </span>
          </div>

          {!isEmailVerified && (
            <div style={{ marginTop: 18 }}>
              {!codeSent ? (
                <button type="button" className="as-ghost-btn" onClick={handleSendCode} disabled={sendingCode}>
                  {sendingCode ? "Sending…" : "Send Verification Code"}
                </button>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <input
                    className="as-input"
                    inputMode="numeric"
                    placeholder="Enter the 6-digit code"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  />
                  <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                    <button
                      type="button"
                      className="as-btn cancel"
                      style={{ flex: 1, padding: "0 10px", letterSpacing: ".16em" }}
                      onClick={() => { setCodeSent(false); setVerifyCode(""); setVerifyError(null); }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="as-btn primary"
                      style={{ flex: 1, padding: "0 10px", letterSpacing: ".16em" }}
                      onClick={handleConfirmCode}
                      disabled={verifying || !verifyCode.trim()}
                    >
                      {verifying ? "Confirming…" : "Confirm"}
                    </button>
                  </div>
                </div>
              )}
              {verifyError && (
                <p style={{ color: A.red, fontSize: 12, marginTop: 12, marginBottom: 0 }}>{verifyError}</p>
              )}
            </div>
          )}
        </div>

        {/* 2FA */}
        <div className="as-card">
          <div className="as-icon-row">
            <span className="as-icon-bead"><Shield size={18} /></span>
            <span className="as-section-title">2-Step Authentication</span>
          </div>
        </div>

        {/* SESSIONS: clickable, opens a modal */}
        <div
          className="as-card clickable"
          role="button"
          tabIndex={0}
          onClick={() => setShowSessionsModal(true)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setShowSessionsModal(true); } }}
        >
          <div className="as-row-between">
            <div className="as-icon-row">
              <span className="as-icon-bead"><LogOut size={18} /></span>
              <div>
                <div className="as-section-title">Active Sessions</div>
                <div className="as-section-sub">See where you're logged in</div>
              </div>
            </div>
            <ChevronRight size={17} color={A.muted} />
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
          {/* DEACTIVATE / REACTIVATE */}
          <button
            type="button"
            className={`as-btn ${isDeactivated ? "success" : "primary"}`}
            disabled={loading}
            onClick={isDeactivated ? handleReactivate : () => setShowDeactivateConfirm(true)}
          >
            <Power size={17} />
            {isDeactivated ? "Reactivate Account" : "Deactivate Account"}
          </button>

          {/* DELETE ACCOUNT */}
          <button type="button" className="as-btn danger" disabled={loading} onClick={() => setShowDeleteConfirm(true)}>
            <Trash2 size={17} />
            {loading ? "Working..." : "Delete Account"}
          </button>
        </div>
      </div>

      {/* DELETE / DEACTIVATE MODAL */}
      <AnimatePresence>
        {(showDeleteConfirm || showDeactivateConfirm) && (
          <motion.div
            className="as-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => { setShowDeleteConfirm(false); setShowDeactivateConfirm(false); }}
          >
            <motion.div
              className="as-alert-card"
              role="dialog"
              aria-modal="true"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={`as-icon-wrap ${showDeleteConfirm ? "danger" : ""}`}>
                <AlertTriangle color={showDeleteConfirm ? A.red : A.ink} size={26} />
              </div>

              <h3 className="as-alert-title">
                {showDeleteConfirm ? "Delete Account?" : "Deactivate Account?"}
              </h3>

              <p className="as-alert-text">
                {showDeleteConfirm
                  ? "This action is permanent and cannot be undone."
                  : "You can reactivate anytime."}
              </p>

              <div className="as-alert-actions">
                <button
                  type="button"
                  className="as-btn cancel"
                  onClick={() => { setShowDeleteConfirm(false); setShowDeactivateConfirm(false); }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`as-btn ${showDeleteConfirm ? "danger" : "primary"}`}
                  disabled={loading}
                  onClick={showDeleteConfirm ? handleDelete : handleDeactivate}
                >
                  {showDeleteConfirm ? "Delete" : "Deactivate"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ACTIVE SESSIONS MODAL */}
      <AnimatePresence>
        {showSessionsModal && (
          <motion.div
            className="as-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowSessionsModal(false)}
          >
            <motion.div
              className="as-alert-card"
              role="dialog"
              aria-modal="true"
              style={{ textAlign: "left", maxWidth: 400 }}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="as-row-between" style={{ marginBottom: 18 }}>
                <span className="as-alert-title" style={{ margin: 0 }}>Active Sessions</span>
                <button type="button" className="as-modal-close" aria-label="Close" onClick={() => setShowSessionsModal(false)}>
                  <X size={16} />
                </button>
              </div>

              <div className="as-session-row">
                <span className="as-session-dot" />
                <div style={{ flex: 1 }}>
                  <div className="as-session-name">{getDeviceLabel()}</div>
                  <div className="as-session-meta">This device · Active now</div>
                </div>
              </div>

              <p style={{ fontSize: 12, color: A.textSoft, margin: "4px 0 0", lineHeight: 1.6 }}>
                Session management for other devices is coming soon. Logging out signs
                out this device only.
              </p>

              <button type="button" className="as-btn cancel" style={{ marginTop: 20, width: "100%" }} onClick={() => setShowSessionsModal(false)}>
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AccountSecurity;